import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Login from "./components/Login";
import Admin from "./Admin";
import Settings from "./Settings";
import Toolbox from "./pages/Toolbox";
import Clients from "./pages/Clients";
import Chat from "./pages/Chat";
import ComputerAgent from "./pages/ComputerAgent";
import DirectoryPage from "./pages/DirectoryPage";
import CreatePage from "./pages/CreatePage";
import MyToolsPage from "./pages/MyToolsPage";
import EditToolPage from "./pages/EditToolPage";
import SuggestionForm from "./pages/SuggestionForm";
import ZipcodeHeatmap from "./pages/ZipcodeHeatmap";
import Transcription from "./pages/Transcription";
import CPDashboard from "./pages/CPDashboard";
import CPForm from "./pages/CPForm";
import CPSessionInfo from "./pages/CPSessionInfo";
import GptSeoTracker from "./pages/GptSeoTracker";
import ReportShell from "./pages/geo/ReportShell";
import TabOverview from "./pages/geo/TabOverview";
import TabPrompts from "./pages/geo/TabPrompts";
import TabResponses from "./pages/geo/TabResponses";
import TabCompetitors from "./pages/geo/TabCompetitors";
import TabSources from "./pages/geo/TabSources";
import TabSentiment from "./pages/geo/TabSentiment";
import TabSettings from "./pages/geo/TabSettings";
import PromptDetail from "./pages/geo/PromptDetail";
import ElementorBuilder from "./pages/ElementorBuilder";
import Search from "./pages/Search";
import ImageGen from "./pages/ImageGen";
import SyntheticAudienceWorkspace from "./pages/SyntheticAudience/SyntheticAudienceWorkspace";
import PersonaEditor from "./pages/SyntheticAudience/PersonaEditor";
import PersonaChat from "./pages/SyntheticAudience/PersonaChat";
import SurveyBuilder from "./pages/SyntheticAudience/SurveyBuilder";
import SurveyResults from "./pages/SyntheticAudience/SurveyResults";
import GooseExport from "./pages/GooseExport";
import WebsiteReport from "./pages/WebsiteReport";
import PublicWebsiteReport from "./pages/PublicWebsiteReport";
import VectorSearch from "./pages/VectorSearch";
import ImpressionHomesContent from "./pages/ImpressionHomesContent";
import ImpressionHomesCCM from "./pages/ImpressionHomesCCM";
import OpsDashboard from "./pages/OpsDashboard";
import LightParkDashboard from "./pages/LightParkDashboard";
import TpStoryHistory from "./pages/TpStoryHistory";
import MattDashboard from "./pages/MattDashboard";
import MiroEstimator from "./pages/MiroEstimator";
import PdfAccessibility from "./pages/PdfAccessibility";
import BasecampFranchiseReporting from "./pages/BasecampFranchiseReporting";
import ClaudeAgentLanding from "./pages/ClaudeAgent/ClaudeAgentLanding";
import ClaudeAgentPage from "./pages/ClaudeAgent/ClaudeAgentPage";
import ClaudeAgentAdminDashboard from "./pages/ClaudeAgent/AdminDashboard";
import ChatbotAdminDashboard from "./pages/ChatbotAdmin/AdminDashboard";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ChatbotProvider } from "./contexts/ChatbotContext";
import { UserStatusProvider } from "./contexts/UserStatusContext";

const NotFound = () => <div>404 - Page Not Found</div>;

// Protected route component that uses the AuthContext
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isCheckingAuth } = useAuth();
  const location = useLocation();

  if (isCheckingAuth) {
    return (
      <div className="auth-loading-container">
        <div className="auth-loading-content">
          <div className="auth-loading-spinner"></div>
          <div className="auth-loading-text">Verifying authentication...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login with the current location stored in state
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render children directly (without cloning)
  return children;
};

// Admin route that checks if user is an admin using AuthContext
const AdminRoute = ({ children }) => {
  const { isAdmin, isCheckingAdmin, isAuthenticated, isCheckingAuth } =
    useAuth();
  const location = useLocation();

  if (isCheckingAuth || isCheckingAdmin) {
    return (
      <div className="auth-loading-container">
        <div className="auth-loading-content">
          <div className="auth-loading-spinner"></div>
          <div className="auth-loading-text">Verifying admin access...</div>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If not admin, redirect to dashboard
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Render children directly
  return children;
};

// Nested inside AdminRoute (isAdmin already guaranteed): also requires the
// Top Manager role. Used only by /chatbot-admin — AdminRoute alone doesn't
// check role, and every other admin page intentionally doesn't need to.
const TopManagerAdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== "Top Manager") {
    return <Navigate to="/" replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Navigate to="/directory?id=681a7c84791eb9a98d7f4660" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/toolbox"
          element={
            <ProtectedRoute>
              <Toolbox />
            </ProtectedRoute>
          }
        />
        <Route
          path="/computer-agent"
          element={
            <ProtectedRoute>
              <ComputerAgent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <Clients />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/claude-agent"
          element={
            <ProtectedRoute>
              <ClaudeAgentLanding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/claude-agent/admin"
          element={
            <AdminRoute>
              <ClaudeAgentAdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/chatbot-admin"
          element={
            <AdminRoute>
              <TopManagerAdminRoute>
                <ChatbotAdminDashboard />
              </TopManagerAdminRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/claude-agent/:agentId"
          element={
            <ProtectedRoute>
              <ClaudeAgentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directory"
          element={
            <ProtectedRoute>
              <DirectoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create"
          element={
            <ProtectedRoute>
              <CreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-tools"
          element={
            <ProtectedRoute>
              <MyToolsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-tool/:toolId"
          element={
            <ProtectedRoute>
              <EditToolPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suggestion-box"
          element={
            <ProtectedRoute>
              <SuggestionForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/zipcode-heatmap"
          element={
            <ProtectedRoute>
              <ZipcodeHeatmap />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audio-transcription"
          element={
            <ProtectedRoute>
              <Transcription />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cp-data-dashboard"
          element={
            <ProtectedRoute>
              <CPDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cycle-planning-feedback"
          element={
            <ProtectedRoute>
              <CPForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gpt-seo-tracking"
          element={
            <ProtectedRoute>
              <GptSeoTracker />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gpt-seo-tracking/reports/:reportId/prompts/:promptNumber"
          element={
            <ProtectedRoute>
              <PromptDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gpt-seo-tracking/reports/:reportId"
          element={
            <ProtectedRoute>
              <ReportShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<TabOverview />} />
          <Route path="prompts" element={<TabPrompts />} />
          <Route path="responses" element={<TabResponses />} />
          <Route path="competitors" element={<TabCompetitors />} />
          <Route path="sources" element={<TabSources />} />
          <Route path="sentiment" element={<TabSentiment />} />
          <Route path="settings" element={<TabSettings />} />
        </Route>
        <Route
          path="/cp-session-info"
          element={
            <ProtectedRoute>
              <CPSessionInfo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/elementor-builder"
          element={
            <ProtectedRoute>
              <ElementorBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />
        <Route
          path="/image-gen"
          element={
            <ProtectedRoute>
              <ImageGen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/synthetic-audience-workshop"
          element={
            <ProtectedRoute>
              <SyntheticAudienceWorkspace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/synthetic-audience-workshop/personas/new"
          element={
            <ProtectedRoute>
              <PersonaEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/synthetic-audience-workshop/personas/:id"
          element={
            <ProtectedRoute>
              <PersonaEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/synthetic-audience-workshop/personas/:id/chat"
          element={
            <ProtectedRoute>
              <PersonaChat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/synthetic-audience-workshop/surveys/new"
          element={
            <ProtectedRoute>
              <SurveyBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/synthetic-audience-workshop/surveys/:id"
          element={
            <ProtectedRoute>
              <SurveyBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/synthetic-audience-workshop/reports/:id/results"
          element={
            <ProtectedRoute>
              <SurveyResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="/website-report"
          element={
            <ProtectedRoute>
              <WebsiteReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/aa_vector_search"
          element={
            <ProtectedRoute>
              <VectorSearch />
            </ProtectedRoute>
          }
        />
        <Route
          path="/impression-homes-content"
          element={
            <ProtectedRoute>
              <ImpressionHomesContent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/impression-homes-ccm"
          element={
            <ProtectedRoute>
              <ImpressionHomesCCM />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ops-dashboard"
          element={
            <ProtectedRoute>
              <OpsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/light-park"
          element={<Navigate to="/light-park-performance-dashboard" replace />}
        />
        <Route
          path="/light-park-performance-dashboard"
          element={
            <ProtectedRoute>
              <LightParkDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tp-story-history"
          element={
            <ProtectedRoute>
              <TpStoryHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/matt-dashboard"
          element={
            <ProtectedRoute>
              <MattDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/miro-estimator"
          element={
            <ProtectedRoute>
              <MiroEstimator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pdf-accessibility"
          element={
            <ProtectedRoute>
              <PdfAccessibility />
            </ProtectedRoute>
          }
        />
        <Route
          path="/basecamp-franchise-reporting"
          element={
            <ProtectedRoute>
              <BasecampFranchiseReporting />
            </ProtectedRoute>
          }
        />
        <Route path="/ai-report/:token" element={<PublicWebsiteReport />} />
        <Route path="/goose-island-312-form" element={<GooseExport />} />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

function App() {
  // Initialize theme on app mount
  useEffect(() => {
    // Helper to get cookie
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };

    // Helper to get system preference
    const getSystemPreference = () => {
      if (window.matchMedia) {
        return window.matchMedia("(prefers-color-scheme: dark)").matches;
      }
      return false;
    };

    // Helper to apply theme
    const applyTheme = (isDark) => {
      document.documentElement.setAttribute(
        "data-theme",
        isDark ? "dark" : "light",
      );
    };

    // Check for manual override first
    const manualTheme = getCookie("theme-manual");

    if (manualTheme) {
      // User has manually set a preference, use it
      applyTheme(manualTheme === "dark");
    } else {
      // No manual preference, use system preference
      const systemPrefersDark = getSystemPreference();
      applyTheme(systemPrefersDark);

      // Listen for system preference changes (only when no manual override)
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = (e) => {
        // Only apply if user hasn't manually set a preference
        if (!getCookie("theme-manual")) {
          applyTheme(e.matches);
        }
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <UserStatusProvider>
          <ChatbotProvider>
            <AppRoutes />
          </ChatbotProvider>
        </UserStatusProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
